import { RawModel } from '@/types/rawModel';
import { useCallback } from 'react';
import { AssetRowDescriptor } from '@/app/assets/components/table/hooks/useAssetsTable';
import { OptimisticAssetsAction } from '@/app/assets/components/table/hooks/useOptimisticAssets';
import { usePendingOutputs } from '@/app/assets/components/table/hooks/usePendingOutputs';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirmationModal } from '@/app/components/modal/ConfirmationModalContext';
import { Stack, Text } from '@mantine/core';
import { deleteRawModels } from '@/app/assets/actions/rawModel';
import { deleteRapidModels } from '@/app/assets/actions/rapidModel';
import { MAIN_ASSET_QUERY_KEY } from '@/app/assets/hooks/useAssetsQuery';
import { useAssetsDownload } from '@/app/assets/components/table/hooks/download/useAssetsGroupDowload';
import { Workflow } from '@/app/workflows/actions/interfaces';
import { showErrorNotification, showWarningNotification } from '@/app/components/shared/notifications';
import { runWorkflow } from '@/app/workflows/actions/runWorkflow';
import { useRowSelection } from '../../../contexts/RowSelectionContext';
import { presetTags } from '@/app/workflows/actions/constants/presetTags';
import { AxiosError } from 'axios';
import { getImpersonation } from '@/utils/impersonation';

export function useAssetGroupActions(
  assets: RawModel[],
  dispatchOptimisticAssetsAction: (action: OptimisticAssetsAction) => void,
  isRawAssetSelected: boolean
) {
  const queryClient = useQueryClient();
  const { startFetchingPendingOutputs, pendingOutputs, addOnJobEndCallback } =
    usePendingOutputs(dispatchOptimisticAssetsAction);
  const { showConfirmationModal } = useConfirmationModal();
  const { rowSelection, clearRowSelection } = useRowSelection();

  const onDeleteAssets = useCallback(async () => {
    const { isImpersonating } = getImpersonation();
    if (isImpersonating) {
      return showErrorNotification({
        title: 'Not allowed',
        message: 'You cannot delete assets while impersonating another user.',
      });
    }
    const selected = Object.entries(rowSelection).map(([key, _]) => JSON.parse(key) as AssetRowDescriptor);

    if (!selected.length) {
      return;
    }

    const rawAssetsToDelete = selected
      .filter((s) => s.type === 'raw')
      .map((s) => ({
        id: s.id,
        name: assets.find((a) => a.id === s.id)?.name,
      }));
    const rapidModelsToDelete = selected
      .filter((s) => {
        if (s.type !== 'output') return false;
        if (s.isMax) return false;
        return !s.isCad;
      })
      .map((s) => ({
        id: s.id,
        name: assets.flatMap((a) => a.rapidModels).find((a) => a.id === s.id)?.name,
      }));

    if (rapidModelsToDelete.length === 0 && rawAssetsToDelete.length === 0) {
      return showWarningNotification({
        message: 'You can only delete Converted Files and DCC Importer output by deleting the source asset.',
      });
    }

    const confirmed = await showConfirmationModal({
      title: 'Deleting models',
      action: 'Delete',
      content: (
        <Stack gap='xs'>
          <Text>Do you really want to delete these models? This action cannot be undone.</Text>
          {!!rawAssetsToDelete.length && (
            <>
              <Text>Source Models:</Text>
              <Text fw={600}>
                {rawAssetsToDelete.reduce((acc, next) => (acc ? `${acc}, ${next.name}` : `${next.name}`), '')}
              </Text>
            </>
          )}
          {!!rapidModelsToDelete.length && (
            <>
              <Text mt={8}>Output Models:</Text>
              <Text fw={600}>
                {rapidModelsToDelete.reduce((acc, next) => (acc ? `${acc}, ${next.name}` : `${next.name}`), '')}
              </Text>
            </>
          )}
        </Stack>
      ),
    });

    if (!confirmed) {
      return;
    }

    dispatchOptimisticAssetsAction({
      action: 'remove',
      payload: rawAssetsToDelete.map((i) => i.id).concat(rapidModelsToDelete.map((i) => i.id)),
    });

    clearRowSelection();

    await Promise.all([
      await deleteRawModels(rawAssetsToDelete.map((i) => i.id)),
      await deleteRapidModels(rapidModelsToDelete.map((r) => r.id)),
    ]);
    await queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.includes(MAIN_ASSET_QUERY_KEY),
    });
  }, [rowSelection, showConfirmationModal, dispatchOptimisticAssetsAction, queryClient, assets, clearRowSelection]);

  const { onDownloadAssets, progress, isPending } = useAssetsDownload(rowSelection, assets);

  const onRunWorkflow = useCallback(
    async (workflow: Workflow) => {
      if (!isRawAssetSelected) {
        return showCantRunWorkflowNotification();
      }

      const selected = Object.entries(rowSelection).map(([key, _]) => JSON.parse(key) as AssetRowDescriptor);

      if (!selected.length) {
        return;
      }

      const rawAssets = selected
        .filter((s) => s.type === 'raw')
        .map((s) => assets.find((a) => a.id === s.id))
        .filter((a) => a);

      const uploadDoneAssets = rawAssets.filter((rawAsset) => rawAsset?.upload_status === 'complete');

      const workflowTags = presetTags.flatMap((p) => p.qualities).find((i) => i.id === workflow.id)?.tags ?? [];
      try {
        await runWorkflow({
          optimizations: uploadDoneAssets.map((asset) => ({
            config: {
              exportName: getExportName({
                assetId: asset!.id,
                assetName: asset!.name,
                workflow,
              }),
              origin: 'web-ui',
              preset_id: workflow.id,
              tags: (asset?.tags?.map((t) => t.name) ?? []).concat(workflowTags),
            },
            model_id: asset!.id,
          })),
        });
      } catch (error) {
        startFetchingPendingOutputs();
        if (error instanceof AxiosError) {
          return showErrorNotification({
            message: error.message,
          });
        }
        return showErrorNotification({
          message: 'An error occurred while running the workflow.',
        });
      }
      startFetchingPendingOutputs();
    },
    [assets, isRawAssetSelected, rowSelection, startFetchingPendingOutputs]
  );

  return {
    pendingOutputs,
    onDeleteAssets,
    onDownloadAssets,
    onRunWorkflow,
    downloadProgress: progress,
    isDownloadPending: isPending,
    addOnJobEndCallback,
  };
}

export const getExportName = ({
  assetId,
  assetName,
  workflow,
}: {
  workflow: Workflow;
  assetName: string;
  assetId: number;
}) => {
  const file = workflow?.meta?.filename;
  return (
    assetName +
    (file?.useId ? '_' + assetId : '') +
    (file?.usePresetName ? '_' + workflow.name : '') +
    (file?.useSuffix?.length ? '_' + file?.useSuffix : '')
  )
    .trim()
    .replaceAll(/\s+/g, '-');
};

export function showCantRunWorkflowNotification() {
  showErrorNotification({
    title: 'Please select a valid source model',
    message:
      'You need to select a valid source model to run a 3D Processor. You cannot use output models, failed imports or pending uploads for this operation.',
  });
}
